/*---------------------------------------------------------------------------------------------------
 *
 *	This page contains the RegressionItemsContainer, which consists of all regression related components.
 *	This page also consists of instructions and the Read More text about overfitting/underfitting.
 *
 * --------------------------------------------------------------------------------------------------
 */

import React from "react";
import PermanentDrawerLeft from "../../components/PermanentDrawerLeft";
import YoutubeComponent from "../../components/Youtube";
import RegressionItemsContainer from "./components/RegressionItemsContainer";
import poly from "../../assets/poly.png";
import poly1 from "../../assets/poly.gif";
import { MathJaxContext, MathJax } from "better-react-mathjax";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  top: {
    backgroundColor: "white",
  },

  content: {
    paddingLeft: "260px",
    paddingTop: "100px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
  },
  regressionSetup: {
    flexDirection: "row",
    display: "flex",
  },
  gif: {
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    justifyContent: "center",
  },
  charts: {
    alignSelf: "center",
    height: "350px",
    width: "800px",
  },
  heading: {
    fontSize: "25px",
  },
  textRegression: {
    textAlign: "justify",
    paddingRight: "30px",
  },
}));

function RegressionPage() {
  const classes = useStyles();

  return (
    <div className={classes.top}>
      <PermanentDrawerLeft></PermanentDrawerLeft>
      <main className={classes.content}>
        <span>
          Hover around the grey grid to observe different X, Y coordinates and
          click on your desired coordinates to add them as points in our
          polynomial linear regression model. These points will then be
          displayed on our chart below. Increase or decrease the "order" value
          to observe changes in the chart. See demo video, examples, and further
          details below!
        </span>
        <div className={classes.regressionSetup}>
          <RegressionItemsContainer />
        </div>
        <div className={classes.gif}>
          <img src={poly1} alt="poly1" />
        </div>
        <img src={poly} alt="charts" className={classes.charts} />
        <br />
        <div className={classes.heading}>
          What's Overfitting and Underfitting?
        </div>
        <br />
        <span className={classes.textRegression}>
          {" "}
          In statistics, "fitting" refers to how well we can approximate a given
          target function. Overfitting occurs when the algorithm or model
          becomes too complex and learns unnecessary fluctuations from a data
          set. An overfitting model fits too well on the training data and
          hence, often fails to return accurate predictions when applied to a
          new unseen data set. On the contrary, underfitting occurs when the
          model can neither fit well on the original data nor generalize well on
          new unseen data. Underfitted models are not suitable for deployment
          and usage.{" "}
        </span>
        <br />
        <div className={classes.heading}>Read More!</div> <br />
        <span className={classes.textRegression}>
          {" "}
          Linear regression depicts the relationship between one or more
          predictor variable(s) (in our case the x coordinate) and an outcome
          variable (the y coordinate). It is often used for predictive analysis.
          For example, it could be used to quantify the relative impact of age
          (x: a predictor variable) on height (y: the outcome variable). Simple
          linear regression is represented by the formula y = mx + c or:
          <MathJaxContext>
            <MathJax dynamic>{"$$y=\\alpha+\\beta x$$"}</MathJax>
          </MathJaxContext>
          Polynomial linear regression is a form of regression where the
          relationship between the independent variable x and the dependent
          variable y is modelled as an nth degree polynomial in x. The value of
          n could be increased or decreased to observe differences in the
          outcome. Polynomial regression is represented by the formula:
          <MathJaxContext>
            <MathJax dynamic>
              {"$$y=\\beta _{0}+\\beta _{1}x+\\beta _{2}x^{2}+\\varepsilon$$"}
            </MathJax>
          </MathJaxContext>
          The charts above show polynomial regression models with different
          degrees or "orders". The true function is the function our regression
          model is trying to learn. It can be used to compare how our current
          regression model is vs how it should actually be. It can be considered
          as a best fit curve. From the first chart, we can see that a linear
          regression function (where the polynomial degree is 1) is not
          sufficient to fit the training sample. The training sample refers to
          the x and y data points we add to our regression function. We can say
          this because the blue "model" line (representing our regression
          function) does not fit well on the orange True Function line and our
          data points. This is called <b>underfitting</b>. This suggests that
          our regression function does not perform well and would not give
          accurate results if used to predict y values by providing x values. A
          polynomial function of degree 4 (as seen in the second chart)
          approximates the true function almost perfectly. However, for higher
          degrees (as seen in the third chart), the model will <b>overfit</b>{" "}
          the training data. This means that the model might become too complex,
          such that it starts describing random errors in our data instead of
          the actual relationships between variables.{" "}
        </span>
        <br />
        <YoutubeComponent link={`https://www.youtube.com/embed/76O32wR4LsA`} />
      </main>
    </div>
  );
}

export default RegressionPage;
